/* Floating "Help" button — sticky bottom-right on every authenticated page.
 *
 * Click → opens a slide-in panel where the user can:
 *   - Submit a new ticket (subject + category + message), OR
 *   - Click "View all tickets" to jump to /app/support
 *
 * The panel shows a red dot badge if any of their tickets are unread.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  LifeBuoy, X, Send, Loader2, MessageSquare, ChevronRight,
} from "lucide-react";

const CATEGORIES = [
  { value: "bug", label: "Bug / Something's broken" },
  { value: "billing", label: "Billing / Subscription" },
  { value: "onboarding", label: "Onboarding Help" },
  { value: "feature_request", label: "Feature Request" },
  { value: "other", label: "Other" },
];


export default function SupportWidget() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    subject: "", category: "other", message: "",
  });
  const [aiAnswer, setAiAnswer] = useState(null);
  const [seeking, setSeeking] = useState(false);
  const [helpful, setHelpful] = useState(null); // null | "yes" | "no"

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/support/unread-count");
      setUnread(data?.count || 0);
    } catch {
      /* silent */
    }
  }, [user]);

  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, 60_000);
    return () => clearInterval(t);
  }, [refreshUnread]);

  // Self-serve auto-answer: 1200ms after the user stops typing, ask Claude
  // if we already have an article that answers them. Only run when both
  // subject + message look substantive enough to matter.
  useEffect(() => {
    if (!open) return;
    const subj = form.subject.trim();
    const msg = form.message.trim();
    if (subj.length < 8 || msg.length < 20) {
      setAiAnswer(null);
      setHelpful(null);
      return;
    }
    setSeeking(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/support/self-serve-answer", {
          subject: subj, message: msg,
        });
        if (data?.has_answer) {
          setAiAnswer(data);
          setHelpful(null);
        } else {
          setAiAnswer(null);
        }
      } catch {
        setAiAnswer(null);
      } finally {
        setSeeking(false);
      }
    }, 1200);
    return () => { clearTimeout(timer); setSeeking(false); };
  }, [open, form.subject, form.message]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.subject.trim().length < 2 || form.message.trim().length < 2) {
      toast.error("Subject and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, page_url: window.location.pathname };
      const { data } = await api.post("/support/tickets", payload);
      toast.success(`Ticket ${data.ref} created. We'll reply within 24h.`);
      setForm({ subject: "", category: "other", message: "" });
      setOpen(false);
      // Take them to the ticket so they see the confirmation.
      nav(`/v3/support/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-[9998] bg-[var(--ink)] hover:bg-black text-white rounded-full shadow-lg hover:shadow-xl transition-all w-14 h-14 flex items-center justify-center group"
        title="Help / Support"
        data-testid="support-fab"
      >
        <LifeBuoy size={22} className="group-hover:rotate-12 transition-transform" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-white"
                data-testid="support-fab-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Slide-in panel */}
      {open && (
        <div className="fixed inset-0 z-[9999] flex justify-end" data-testid="support-panel">
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <div className="relative w-full max-w-md bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200">
            <div className="sticky top-0 bg-white border-b border-[var(--line)] px-5 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <LifeBuoy size={20} className="text-emerald-600" />
                <h2 className="font-display font-extrabold text-lg">Get Help</h2>
              </div>
              <button
                type="button" onClick={() => setOpen(false)}
                className="p-1 hover:bg-stone-100 rounded"
                data-testid="support-panel-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900">
                <div className="font-bold mb-0.5">Support reads every one.</div>
                Expect a real reply within 24 hours — usually much faster.
              </div>

              <form onSubmit={submit} className="space-y-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)]">
                    What&apos;s up?
                  </label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={set("subject")}
                    placeholder="Short summary..."
                    className="input w-full mt-1"
                    maxLength={140}
                    required
                    data-testid="support-subject"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)]">
                    Category
                  </label>
                  <select
                    value={form.category}
                    onChange={set("category")}
                    className="input w-full mt-1"
                    data-testid="support-category"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[var(--ink-2)]">
                    Tell us more
                  </label>
                  <textarea
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Steps to reproduce, screenshots (paste a Loom/Imgur URL), what you expected..."
                    rows={7}
                    className="input w-full mt-1 resize-none"
                    maxLength={8000}
                    required
                    data-testid="support-message"
                  />
                  <div className="text-[10px] text-[var(--ink-3)] mt-1">
                    Tip: paste a Loom URL or Imgur screenshot link for fastest resolution.
                  </div>
                </div>

                {(seeking || aiAnswer) && (
                  <div
                    className={
                      "rounded-lg border p-3 " +
                      (aiAnswer ? "bg-violet-50 border-violet-200" : "bg-slate-50 border-slate-200")
                    }
                    data-testid="support-self-serve-card"
                  >
                    {seeking && !aiAnswer ? (
                      <div className="flex items-center gap-2 text-xs text-slate-600">
                        <Loader2 size={12} className="animate-spin text-violet-600" />
                        <span>Looking for an answer in our help articles…</span>
                      </div>
                    ) : aiAnswer && helpful !== "no" ? (
                      <div>
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-violet-700 mb-1.5">
                          <span>✨ Possible answer</span>
                          <span className="text-violet-400">·</span>
                          <span className="text-violet-500">{aiAnswer.confidence}</span>
                        </div>
                        <p className="text-sm text-slate-800 leading-relaxed" data-testid="support-self-serve-answer">
                          {aiAnswer.answer}
                        </p>
                        {aiAnswer.source_url && (
                          <a
                            href={aiAnswer.source_url}
                            target="_blank" rel="noopener noreferrer"
                            className="text-xs text-violet-700 hover:text-violet-900 font-semibold underline mt-2 inline-block"
                            data-testid="support-self-serve-source"
                          >
                            Read full article: {aiAnswer.source_title} →
                          </a>
                        )}
                        {helpful === null && (
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-violet-200">
                            <span className="text-xs text-slate-600">Did this answer your question?</span>
                            <button
                              type="button"
                              onClick={() => {
                                setHelpful("yes");
                                toast.success("Great — glad we could help!");
                                setTimeout(() => { setOpen(false); setForm({ subject: "", category: "other", message: "" }); setAiAnswer(null); setHelpful(null); }, 1200);
                              }}
                              className="text-xs font-bold px-2.5 py-1 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white"
                              data-testid="support-self-serve-yes"
                            >
                              Yes, thanks
                            </button>
                            <button
                              type="button"
                              onClick={() => setHelpful("no")}
                              className="text-xs font-bold px-2.5 py-1 rounded-md bg-white hover:bg-slate-100 border border-slate-300 text-slate-700"
                              data-testid="support-self-serve-no"
                            >
                              No, file ticket
                            </button>
                          </div>
                        )}
                        {helpful === "yes" && (
                          <div className="text-xs text-emerald-700 font-semibold mt-3">Closing in a moment…</div>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold text-sm py-2.5 rounded-lg inline-flex items-center justify-center gap-2"
                  data-testid="support-submit"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Send to Support
                </button>
              </form>

              <button
                type="button"
                onClick={() => { setOpen(false); nav("/v3/support"); }}
                className="w-full mt-2 text-sm text-[var(--ink-2)] hover:text-[var(--ink)] font-semibold inline-flex items-center justify-center gap-1 py-2 border-t border-[var(--line)] pt-3"
                data-testid="support-view-all"
              >
                <MessageSquare size={14} /> View all my tickets <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
